import { Link } from 'react-router-dom';
import { MemberProfile } from '../../../mocks/dashboard';

export type DashSection = 'overview' | 'deals' | 'tax-records';

interface NavItem {
  id: DashSection | 'external' | 'link';
  label: string;
  icon: string;
  href?: string;
  to?: string;
  section?: DashSection;
}

const NAV_GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: 'Main',
    items: [
      { id: 'overview', label: 'Overview',        icon: 'ri-dashboard-3-line', section: 'overview' },
      { id: 'deals',    label: 'My Deals',         icon: 'ri-building-2-line',  section: 'deals'    },
    ],
  },
  {
    label: 'State Maps',
    items: [
      { id: 'external', label: 'CA Map', icon: 'ri-map-2-line', href: 'https://businessdevelopmentauthority.net/ca-tax-foreclosures' },
      { id: 'external', label: 'NV Map', icon: 'ri-map-2-line', href: 'https://businessdevelopmentauthority.net/nv-tax-foreclosures' },
      { id: 'external', label: 'AZ Map', icon: 'ri-map-2-line', href: 'https://businessdevelopmentauthority.net/tax-foreclosures'    },
    ],
  },
  {
    label: 'Data',
    items: [
      { id: 'tax-records', label: 'Tax Records', icon: 'ri-file-list-3-line', section: 'tax-records' },
      { id: 'link',        label: 'Resources',   icon: 'ri-book-open-line',   to: '/resources'       },
    ],
  },
  {
    label: 'Account',
    items: [
      { id: 'link', label: 'Account Settings', icon: 'ri-settings-3-line', to: '/contact' },
    ],
  },
];

const PLAN_COLORS: Record<string, string> = {
  Starter: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  Pro:     'bg-gold/20 text-gold border-gold/30',
  Elite:   'bg-white/10 text-white border-white/20',
};

interface Props {
  active: DashSection;
  onSelect: (s: DashSection) => void;
  member: MemberProfile;
  collapsed: boolean;
  onToggle: () => void;
}

export default function DashboardSidebar({ active, onSelect, member, collapsed, onToggle }: Props) {
  return (
    <aside
      className={`flex flex-col bg-[#0d1520] border-r border-white/5 transition-all duration-300 flex-shrink-0 ${
        collapsed ? 'w-16' : 'w-64'
      }`}
    >
      {/* Logo + collapse toggle */}
      <div className={`flex items-center justify-between px-4 py-5 border-b border-white/5 ${collapsed ? 'flex-col gap-3' : ''}`}>
        {!collapsed && (
          <Link to="/" className="flex-shrink-0">
            <img
              src="https://public.readdy.ai/ai/img_res/e4baf86f-3d4f-4e4d-945f-ebc375a864af.png"
              alt="Foreclosure Recovery"
              className="h-9 w-auto object-contain"
            />
          </Link>
        )}
        <button
          onClick={onToggle}
          className="w-7 h-7 flex items-center justify-center rounded-md text-white/30 hover:text-white/70 hover:bg-white/5 transition-colors cursor-pointer flex-shrink-0"
          aria-label="Toggle sidebar"
        >
          <i className={`ri-${collapsed ? 'menu-unfold' : 'menu-fold'}-line text-base`} />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        {NAV_GROUPS.map((group) => (
          <div key={group.label} className="mb-5">
            {!collapsed && (
              <p className="text-white/25 text-[10px] font-bold uppercase tracking-widest px-3 mb-2">
                {group.label}
              </p>
            )}
            <ul className="flex flex-col gap-0.5">
              {group.items.map((item, idx) => {
                const isActive = 'section' in item && item.section === active;

                /* External link */
                if (item.href) {
                  const stateTag = item.label.split(' ')[0]; // "CA", "NV", "AZ"
                  return (
                    <li key={idx}>
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={collapsed ? item.label : undefined}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/50 hover:text-white hover:bg-white/5 transition-colors group cursor-pointer"
                      >
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className={`${item.icon} text-base`} />
                        </div>
                        {!collapsed && (
                          <>
                            <span className="text-sm flex-1">{item.label}</span>
                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${
                              stateTag === 'CA' ? 'bg-amber-500/20 text-amber-400' :
                              stateTag === 'NV' ? 'bg-red-500/20 text-red-400' :
                              'bg-orange-500/20 text-orange-400'
                            }`}>
                              {stateTag}
                            </span>
                            <i className="ri-external-link-line text-xs text-white/20 flex-shrink-0" />
                          </>
                        )}
                      </a>
                    </li>
                  );
                }

                /* Internal react-router link */
                if (item.to) {
                  return (
                    <li key={idx}>
                      <Link
                        to={item.to}
                        title={collapsed ? item.label : undefined}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/50 hover:text-white hover:bg-white/5 transition-colors"
                      >
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className={`${item.icon} text-base`} />
                        </div>
                        {!collapsed && <span className="text-sm">{item.label}</span>}
                      </Link>
                    </li>
                  );
                }

                /* Section switch */
                return (
                  <li key={idx}>
                    <button
                      onClick={() => item.section && onSelect(item.section)}
                      title={collapsed ? item.label : undefined}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors cursor-pointer ${
                        isActive
                          ? 'bg-gold/15 text-gold border border-gold/20'
                          : 'text-white/50 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-base`} />
                      </div>
                      {!collapsed && (
                        <span className="text-sm text-left flex-1">{item.label}</span>
                      )}
                      {!collapsed && isActive && (
                        <div className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Member Profile */}
      <div className={`border-t border-white/5 p-3 ${collapsed ? 'flex justify-center' : ''}`}>
        {collapsed ? (
          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gold/20 border border-gold/30 text-gold text-xs font-bold flex-shrink-0">
            {member.avatarInitials}
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gold/20 border border-gold/30 text-gold text-sm font-bold flex-shrink-0">
              {member.avatarInitials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-semibold truncate">
                {member.firstName} {member.lastName}
              </p>
              <span className={`inline-block text-xs font-bold px-2 py-0.5 rounded-full border mt-0.5 ${PLAN_COLORS[member.plan]}`}>
                {member.plan}
              </span>
            </div>
            <Link to="/login" title="Sign out" className="w-7 h-7 flex items-center justify-center text-white/25 hover:text-white/70 transition-colors flex-shrink-0">
              <i className="ri-logout-box-r-line text-sm" />
            </Link>
          </div>
        )}
      </div>
    </aside>
  );
}
